import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useProject } from '@/contexts/ProjectContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArrowLeft, RefreshCw, Loader2, Truck, BarChart3, TrendingUp,
  DollarSign, AlertTriangle, Gauge, Clock, Wrench, Plus, X, Activity, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { AgGridReact } from 'ag-grid-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, Cell
} from 'recharts';
import IoTHealthMonitor from '@/components/iot/IoTHealthMonitor';
import ModuleInsightsDashboard from '@/components/common/ModuleInsightsDashboard';

function fmt(val) {
  if (val === undefined || val === null || val === 0) return '0';
  if (Math.abs(val) >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (Math.abs(val) >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

const UTIL_COLOR = (pct) => pct >= 75 ? '#22C55E' : pct >= 50 ? '#F59E0B' : '#EF4444';

function KPICard({ title, value, subtitle, icon: Icon, color = '#0EA5E9' }) {
  return (
    <div className="rounded-xl p-4" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
      style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <div className="flex items-start justify-between mb-2">
        <div className="p-2 rounded-lg" style={{ background: `${color}12`, border: `1px solid ${color}20` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{value}</div>
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{title}</div>
      {subtitle && <div className="text-[10px] mt-1" style={{ color: 'var(--text-disabled)' }}>{subtitle}</div>}
    </div>
  );
}

const CATEGORIES = ['CRANE', 'EXCAVATOR', 'LOADER', 'DUMP_TRUCK', 'FORKLIFT', 'GENERATOR', 'COMPRESSOR', 'PILING_RIG', 'CONCRETE_PUMP', 'WELDING_SET', 'SCAFFOLDING', 'TRANSPORT', 'OTHER'];
const OWNERSHIP_TYPES = [{ value: 'OWNED', label: 'Owned' }, { value: 'HIRED_DRY', label: 'Dry Hire' }, { value: 'HIRED_WET', label: 'Wet Hire' }];
const RATE_BASES = ['DAILY', 'HOURLY', 'MONTHLY'];

function CreateEquipmentModal({ projectId, onCreated, onClose }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    equipmentCode: '', equipmentName: '', equipmentCategory: 'CRANE',
    ownershipType: 'HIRED_DRY', vendorName: '', rateBasis: 'DAILY',
    dailyRate: '', hourlyRate: '', monthlyRate: '', mobilizationCost: '', totalPlannedCost: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inputCls = "w-full rounded-lg px-3 py-2 text-sm";
  const inputStyle = { backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' };
  const labelCls = "text-xs font-medium mb-1 block";
  const labelStyle = { color: 'var(--text-muted)' };

  const handleSubmit = async () => {
    if (!form.equipmentCode.trim() || !form.equipmentName.trim()) { toast.error('Code and Name are required'); return; }
    setSaving(true);
    try {
      await api.post(`/v1/projects/${projectId}/cc/equipment`, {
        ...form,
        dailyRate: form.dailyRate ? +form.dailyRate : null,
        hourlyRate: form.hourlyRate ? +form.hourlyRate : null,
        monthlyRate: form.monthlyRate ? +form.monthlyRate : null,
        mobilizationCost: +form.mobilizationCost || 0,
        totalPlannedCost: +form.totalPlannedCost || 0,
      });
      toast.success(`Equipment ${form.equipmentCode} created`);
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 bg-black/40" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl shadow-2xl overflow-y-auto max-h-[90vh]" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} onClick={e => e.stopPropagation()} data-testid="create-equipment-modal">
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Add Equipment</h2>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className={labelCls} style={labelStyle}>Equipment Code *</span>
              <input value={form.equipmentCode} onChange={e => set('equipmentCode', e.target.value)} className={inputCls} style={inputStyle} placeholder="e.g. CRN-MOB-100T-01" data-testid="equip-code-input" />
            </div>
            <div>
              <span className={labelCls} style={labelStyle}>Equipment Name *</span>
              <input value={form.equipmentName} onChange={e => set('equipmentName', e.target.value)} className={inputCls} style={inputStyle} placeholder="e.g. 100T Mobile Crane" data-testid="equip-name-input" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <span className={labelCls} style={labelStyle}>Category</span>
              <select value={form.equipmentCategory} onChange={e => set('equipmentCategory', e.target.value)} className={inputCls} style={inputStyle} data-testid="equip-category-select">
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <span className={labelCls} style={labelStyle}>Ownership Type</span>
              <select value={form.ownershipType} onChange={e => set('ownershipType', e.target.value)} className={inputCls} style={inputStyle} data-testid="equip-type-select">
                {OWNERSHIP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <span className={labelCls} style={labelStyle}>Vendor</span>
              <input value={form.vendorName} onChange={e => set('vendorName', e.target.value)} className={inputCls} style={inputStyle} placeholder="Vendor name" data-testid="equip-vendor-input" />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <span className={labelCls} style={labelStyle}>Rate Basis</span>
              <select value={form.rateBasis} onChange={e => set('rateBasis', e.target.value)} className={inputCls} style={inputStyle} data-testid="equip-ratebasis-select">
                {RATE_BASES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <span className={labelCls} style={labelStyle}>Daily Rate (₹)</span>
              <input type="number" value={form.dailyRate} onChange={e => set('dailyRate', e.target.value)} className={inputCls} style={inputStyle} placeholder="0" data-testid="equip-daily-rate" />
            </div>
            <div>
              <span className={labelCls} style={labelStyle}>Hourly Rate (₹)</span>
              <input type="number" value={form.hourlyRate} onChange={e => set('hourlyRate', e.target.value)} className={inputCls} style={inputStyle} placeholder="0" data-testid="equip-hourly-rate" />
            </div>
            <div>
              <span className={labelCls} style={labelStyle}>Monthly Rate (₹)</span>
              <input type="number" value={form.monthlyRate} onChange={e => set('monthlyRate', e.target.value)} className={inputCls} style={inputStyle} placeholder="0" data-testid="equip-monthly-rate" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className={labelCls} style={labelStyle}>Mobilization Cost (₹)</span>
              <input type="number" value={form.mobilizationCost} onChange={e => set('mobilizationCost', e.target.value)} className={inputCls} style={inputStyle} placeholder="0" data-testid="equip-mob-cost" />
            </div>
            <div>
              <span className={labelCls} style={labelStyle}>Total Planned Cost (₹)</span>
              <input type="number" value={form.totalPlannedCost} onChange={e => set('totalPlannedCost', e.target.value)} className={inputCls} style={inputStyle} placeholder="0" data-testid="equip-planned-cost" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={handleSubmit} disabled={saving} data-testid="equip-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Create
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SwmCognaraEquipmentTab({ projectId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.get(`/v1/projects/${projectId}/cc/equipment`)
      .then(r => {
        const items = Array.isArray(r.data) ? r.data : (r.data?.equipment || []);
        // Keep only entries that carry SWM reservations (from Cognara push)
        setRows(items.filter(e => (e.reservations || []).some(x => x.source === 'cognara-hitl')));
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  const flatReservations = useMemo(() => {
    const out = [];
    rows.forEach(e => (e.reservations || [])
      .filter(r => r.source === 'cognara-hitl')
      .forEach(r => out.push({ ...r, equipmentCode: e.equipmentCode, equipmentName: e.equipmentName,
                                capacityTon: e.capacityTon, category: e.equipmentCategory })));
    return out.sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
  }, [rows]);

  if (loading) return <div className="rounded-xl p-8 text-center text-sm" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-muted)' }}><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading Cognara SWM reservations…</div>;
  if (flatReservations.length === 0) return <div className="rounded-xl p-8 text-center text-sm" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>No SWM crane reservations yet. Go to SWM → select tags → "Cognara: MP & Equipment" → push. Reservations from those pushes appear here.</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-muted)' }}>
        <span className="px-2 py-1 rounded font-mono font-semibold" style={{ background: '#6366F122', color: '#6366F1' }}>{flatReservations.length} SWM reservation{flatReservations.length === 1 ? '' : 's'}</span>
        <span>across {rows.length} equipment{rows.length === 1 ? '' : 's'} in the register</span>
      </div>
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        <table className="w-full text-xs" data-testid="swm-cognara-equip-table">
          <thead style={{ background: 'var(--card-bg-alt)' }}>
            <tr>
              <th className="text-left p-2">Equipment</th>
              <th className="text-left p-2">Reserved For</th>
              <th className="text-left p-2">Window</th>
              <th className="text-right p-2">Capacity · Qty</th>
            </tr>
          </thead>
          <tbody>
            {flatReservations.map((r, i) => (
              <tr key={i} className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                <td className="p-2">
                  <div className="font-mono text-[10px] font-semibold" style={{ color: '#10B981' }}>{r.equipmentCode}</div>
                  <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{r.equipmentName || r.category}</div>
                </td>
                <td className="p-2">
                  <div className="font-mono text-[10px] font-semibold" style={{ color: '#0EA5E9' }}>{r.structuralTag}</div>
                  <div className="font-mono text-[10px]" style={{ color: 'var(--text-muted)' }}>{r.markNumber}</div>
                </td>
                <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  {r.startDate ? new Date(r.startDate).toLocaleDateString() : '—'} → {r.endDate ? new Date(r.endDate).toLocaleDateString() : '—'}
                </td>
                <td className="p-2 text-right text-[10px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {r.capacityTon ? `${r.capacityTon} t` : '—'} · {r.qty || 1}×
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export default function EquipmentManagement() {
  const { projectId: ctxProjectId } = useProject();
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [utilData, setUtilData] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('fleet');
  const [showCreate, setShowCreate] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await api.get('/v1/cc/projects-with-data');
      setProjects(data.projects || []);
      if (data.projects?.length > 0 && !selectedProject) setSelectedProject(data.projects[0].project_id);
    } catch { toast.error('Failed to load projects'); }
  }, [selectedProject]);

  const fetchAll = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/projects/${selectedProject}/cc/equipment/utilization-summary`);
      setUtilData(data);
    } catch { toast.error('Failed to load equipment data'); }
    finally { setLoading(false); }
  }, [selectedProject]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { if (selectedProject) fetchAll(); }, [selectedProject, fetchAll]);

  const summary = utilData?.summary || {};
  const equipment = utilData?.equipment || [];
  const heatmap = utilData?.heatmap || [];

  // Collect all months from heatmap
  const allMonths = useMemo(() => {
    const months = new Set();
    heatmap.forEach(e => Object.keys(e.months || {}).forEach(m => months.add(m)));
    return Array.from(months).sort();
  }, [heatmap]);

  // AG Grid columns
  const equipColumnDefs = useMemo(() => [
    { headerName: 'Code', field: 'equipmentCode', width: 160, pinned: 'left' },
    { headerName: 'Name', field: 'equipmentName', width: 200 },
    { headerName: 'Category', field: 'equipmentCategory', width: 120 },
    { headerName: 'Type', field: 'ownershipType', width: 110,
      valueFormatter: p => ({ OWNED: 'Owned', HIRED_DRY: 'Dry Hire', HIRED_WET: 'Wet Hire' }[p.value] || p.value || '—'),
      cellStyle: p => ({ color: ({ OWNED: '#22C55E', HIRED_DRY: '#3B82F6', HIRED_WET: '#143A6B' }[p.value] || '#6B7280'), fontWeight: '600' })
    },
    { headerName: 'Vendor', field: 'vendorName', width: 140 },
    { headerName: 'Rate Basis', field: 'rates.rateBasis', width: 90 },
    { headerName: 'Rate', width: 100, type: 'numericColumn',
      valueGetter: p => {
        const r = p.data?.rates || {};
        return r.dailyRate || r.hourlyRate || r.monthlyRate || 0;
      },
      valueFormatter: p => `₹${(p.value || 0).toLocaleString('en-IN')}`
    },
    { headerName: 'Cal Days', field: 'utilization.totalCalendarDays', width: 80, type: 'numericColumn' },
    { headerName: 'Op Hours', field: 'utilization.totalOperatingHours', width: 90, type: 'numericColumn',
      valueFormatter: p => (p.value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 }) },
    { headerName: 'Util %', field: 'utilization.utilizationPct', width: 80, type: 'numericColumn',
      cellStyle: p => ({ color: UTIL_COLOR(p.value || 0), fontWeight: '700' }),
      valueFormatter: p => `${(p.value || 0).toFixed(1)}%` },
    { headerName: 'Planned Cost', field: 'totalPlannedCost', width: 120, type: 'numericColumn', valueFormatter: p => fmt(p.value) },
    { headerName: 'Actual Cost', field: 'totalActualCost', width: 120, type: 'numericColumn', valueFormatter: p => fmt(p.value) },
    { headerName: 'Variance', field: 'costVariance', width: 120, type: 'numericColumn',
      valueFormatter: p => fmt(p.value),
      cellStyle: p => ({ color: (p.value || 0) >= 0 ? '#22C55E' : '#EF4444', fontWeight: '600' }) },
  ], []);

  // Utilization chart data (by category)
  const catData = useMemo(() => {
    const cats = {};
    equipment.forEach(e => {
      const cat = e.equipmentCategory;
      if (!cats[cat]) cats[cat] = { category: cat, planned: 0, actual: 0, count: 0, avgUtil: 0 };
      cats[cat].planned += e.totalPlannedCost || 0;
      cats[cat].actual += e.totalActualCost || 0;
      cats[cat].count += 1;
      cats[cat].avgUtil += e.utilization?.utilizationPct || 0;
    });
    return Object.values(cats).map(c => ({ ...c, avgUtil: c.count > 0 ? Math.round(c.avgUtil / c.count) : 0 }));
  }, [equipment]);

  return (
    <DashboardLayout>
      {ctxProjectId && <AIAnalystPanel projectId={ctxProjectId} moduleKey="EQUIPMENT_MGMT" defaultOpen={true} compact={true} />}

      <div className="p-6 space-y-5" data-testid="equipment-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/project-controls')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="p-2.5 rounded-xl" style={{ background: '#10B98112', border: '1px solid #10B98120' }}>
              <Truck className="w-5 h-5" style={{ color: '#10B981' }} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Equipment Management</h1>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Fleet register, utilization tracking, cost analysis</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select className="text-sm rounded-lg px-3 py-1.5"
              style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
              value={selectedProject || ''} onChange={e => setSelectedProject(e.target.value)} data-testid="project-selector">
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
            </select>
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="add-equipment-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Equipment
            </Button>
            <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} />
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-5 gap-3">
              <KPICard title="Total Fleet" value={summary.totalFleet || 0} subtitle="units" icon={Truck} color="#10B981" />
              <KPICard title="Active Mobilized" value={summary.activeMobilized || 0} icon={Wrench} color="#3B82F6" />
              <KPICard title="Avg Utilization" value={`${summary.avgUtilizationPct || 0}%`} icon={Gauge}
                color={UTIL_COLOR(summary.avgUtilizationPct || 0)} />
              <KPICard title="Total Actual Cost" value={fmt(summary.totalActualCost)} icon={DollarSign} color="#F59E0B" />
              <KPICard title="Cost Variance" value={fmt(summary.totalVariance)}
                icon={summary.totalVariance >= 0 ? TrendingUp : AlertTriangle}
                color={summary.totalVariance >= 0 ? '#22C55E' : '#EF4444'} />
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-0.5 rounded-lg" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
              {[
                { id: 'fleet', label: 'Fleet Register', icon: Truck },
                { id: 'insights', label: 'AI Insights', icon: Sparkles },
                { id: 'heatmap', label: 'Utilization Heatmap', icon: BarChart3 },
                { id: 'cost', label: 'Cost Analysis', icon: DollarSign },
                { id: 'iot', label: 'IoT Health Monitor', icon: Activity },
                { id: 'swm-plan', label: 'Cognara SWM Plan', icon: Sparkles },
              ].map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-md transition-all"
                  style={{
                    backgroundColor: activeTab === t.id ? '#10B98115' : 'transparent',
                    color: activeTab === t.id ? '#10B981' : 'var(--text-muted)',
                    fontWeight: activeTab === t.id ? '600' : '400'
                  }} data-testid={`tab-${t.id}`}>
                  <t.icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              ))}
            </div>

            {activeTab === 'insights' && (
              <ModuleInsightsDashboard projectId={selectedProject} moduleKey="equipment"
                color="#F97316" deepLinkBase="/project-controls/cost/equipment" />
            )}

            {activeTab === 'fleet' && (
              <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }} data-testid="equipment-grid">
                  <AgGridReact
                    ref={gridRef}
                    columnDefs={equipColumnDefs}
                    rowData={equipment}
                    defaultColDef={{ sortable: true, resizable: true, cellStyle: { fontSize: '12px' } }}
                    animateRows={true}
                    headerHeight={36}
                    rowHeight={34}
                  />
                </div>
              </div>
            )}

            {activeTab === 'heatmap' && (
              <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                  Equipment Utilization Heatmap (% per month)
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="utilization-heatmap">
                    <thead>
                      <tr>
                        <th className="text-left p-2 sticky left-0" style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text-muted)', minWidth: 180 }}>Equipment</th>
                        {allMonths.map(m => (
                          <th key={m} className="text-center p-2" style={{ color: 'var(--text-muted)', minWidth: 60 }}>{m.slice(2)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {heatmap.map((eq, idx) => (
                        <tr key={idx} style={{ borderTop: '1px solid var(--border-color)' }}>
                          <td className="p-2 font-medium sticky left-0" style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}>
                            <div className="truncate">{eq.equipmentName}</div>
                            <div style={{ color: 'var(--text-disabled)' }}>{eq.equipmentCode}</div>
                          </td>
                          {allMonths.map(m => {
                            const util = eq.months?.[m]?.utilPct || 0;
                            const bg = util === 0 ? 'var(--table-header-bg)' : UTIL_COLOR(util);
                            const opacity = util === 0 ? 0.3 : Math.max(0.3, Math.min(1, util / 100));
                            return (
                              <td key={m} className="text-center p-2 font-semibold" style={{ backgroundColor: `${bg}${Math.round(opacity * 40).toString(16).padStart(2, '0')}`, color: util > 0 ? bg : 'var(--text-disabled)' }}>
                                {util > 0 ? `${util}%` : '—'}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Legend */}
                <div className="flex items-center gap-4 mt-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ backgroundColor: '#EF4444' }} /> &lt;50% (Low)</div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ backgroundColor: '#F59E0B' }} /> 50-75% (Medium)</div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ backgroundColor: '#22C55E' }} /> &gt;75% (Good)</div>
                </div>
              </div>
            )}

            {activeTab === 'cost' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                    Cost by Equipment Category
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={catData} margin={{ bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                      <XAxis dataKey="category" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" />
                      <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={v => fmt(v)} />
                      <Legend />
                      <Bar dataKey="planned" name="Planned Cost" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="actual" name="Actual Cost" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                  <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                    Avg Utilization by Category
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={catData} layout="vertical" margin={{ left: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                      <YAxis type="category" dataKey="category" tick={{ fontSize: 10 }} width={75} />
                      <Tooltip formatter={v => `${v}%`} />
                      <Bar dataKey="avgUtil" name="Avg Utilization" radius={[0, 4, 4, 0]}>
                        {catData.map((d, i) => (
                          <Cell key={i} fill={UTIL_COLOR(d.avgUtil)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            {activeTab === 'iot' && selectedProject && (
              <IoTHealthMonitor projectId={selectedProject} />
            )}
            {activeTab === 'swm-plan' && selectedProject && (
              <SwmCognaraEquipmentTab projectId={selectedProject} />
            )}
          </>
        )}

        {showCreate && selectedProject && (
          <CreateEquipmentModal
            projectId={selectedProject}
            onCreated={() => { setShowCreate(false); fetchAll(); }}
            onClose={() => setShowCreate(false)}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
